import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import LandingPage from './components/LandingPage';
import Loading from './components/Loading';
import { useToast } from './components/ToastProvider';
import './App.css';
import {
  COOKIE_CONSENT_ACCEPT_ALL,
  COOKIE_CONSENT_ESSENTIAL_ONLY,
  COOKIE_CONSENT_UPDATED_EVENT,
  canLoadAnalytics,
  getCookieConsentPreference,
  persistCookieConsentPreference,
} from './utils/cookieConsent';
import {
  getCurrentPageFromLocation,
  navigateToPage,
  upgradeLegacyHashRoute,
} from './utils/navigation';

const AUTH_REQUIRED_PAGES = new Set([
  'question-bank',
  'resume-analyzer',
  'dashboard',
]);
const STANDALONE_APP_PAGES = new Set([
  'question-bank',
  'resume-analyzer',
  'verify',
]);
const DEFAULT_GA_MEASUREMENT_ID = 'G-GM04ZD44ZL';
const AUTH_SESSION_HINT_KEY = 'tryinterview:last-authenticated';

function lazyWithPreload(loader) {
  const Component = lazy(loader);
  Component.preload = loader;
  return Component;
}

function preloadComponent(Component) {
  if (typeof Component?.preload === 'function') {
    void Component.preload();
  }
}

function preloadComponents(components) {
  components.forEach((Component) => preloadComponent(Component));
}

function scheduleDeferredTask(callback, { timeout = 1800, delay = 140 } = {}) {
  if (typeof window === 'undefined') {
    callback();
    return () => {};
  }

  if ('requestIdleCallback' in window) {
    const idleCallbackId = window.requestIdleCallback(callback, { timeout });

    return () => {
      if ('cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
    };
  }

  const timeoutId = window.setTimeout(callback, delay);
  return () => window.clearTimeout(timeoutId);
}

function requiresAuth(page = 'home') {
  return AUTH_REQUIRED_PAGES.has(page);
}

function isStandaloneAppPage(page = 'home') {
  return STANDALONE_APP_PAGES.has(page);
}

function hasCompletedProfileField(value) {
  if (Array.isArray(value)) {
    return value.some((item) => Boolean(String(item || '').trim()));
  }

  return Boolean(String(value || '').trim());
}

function shouldRequireOnboarding(profile, authenticatedUser) {
  if (!profile) {
    return true;
  }

  if (profile.onboarded !== true) {
    return true;
  }

  const completedCoreFieldCount = [
    profile.name || authenticatedUser?.displayName || '',
    profile.photoURL || authenticatedUser?.photoURL || '',
    profile.country,
    profile.preferredLanguage,
    profile.jobTitle,
    profile.targetRole,
    profile.experience,
    profile.interviewType,
  ].filter(hasCompletedProfileField).length;

  return completedCoreFieldCount < 6;
}

const AuthModal = lazyWithPreload(() => import('./components/AuthModal'));
const Dashboard = lazyWithPreload(() => import('./components/Dashboard'));
const Features = lazyWithPreload(() => import('./components/Features'));
const Founder = lazyWithPreload(() => import('./components/Founder'));
const Terms = lazyWithPreload(() => import('./components/Terms'));
const About = lazyWithPreload(() => import('./components/About'));
const Privacy = lazyWithPreload(() => import('./components/Privacy'));
const QuestionBank = lazyWithPreload(() => import('./components/QuestionBank'));
const ResumeAnalyzer = lazyWithPreload(() => import('./components/ResumeAnalyzer'));
const Contact = lazyWithPreload(() => import('./components/Contact'));
const Help = lazyWithPreload(() => import('./components/Help'));
const FAQ = lazyWithPreload(() => import('./components/FAQ'));
const Cookies = lazyWithPreload(() => import('./components/Cookies'));
const CookieConsentBanner = lazyWithPreload(() => import('./components/CookieConsentBanner'));
const Pricing = lazyWithPreload(() => import('./components/Pricing'));
const Sitemap = lazyWithPreload(() => import('./components/Sitemap'));
const OnboardingModal = lazyWithPreload(() => import('./components/OnboardingModal'));
const PaymentSuccess = lazyWithPreload(() => import('./components/PaymentSuccess'));
const CertificateVerification = lazyWithPreload(() => import('./components/CertificateVerification'));

const PUBLIC_ROUTE_COMPONENTS = [
  Features,
  Founder,
  Terms,
  About,
  Privacy,
  Contact,
  Help,
  FAQ,
  Cookies,
  Pricing,
  Sitemap,
  CertificateVerification,
];
const SECURE_ROUTE_COMPONENTS = [
  AuthModal,
  Dashboard,
  QuestionBank,
  ResumeAnalyzer,
  OnboardingModal,
  PaymentSuccess,
];

let firebaseClientPromise;

function loadFirebaseClient() {
  if (!firebaseClientPromise) {
    firebaseClientPromise = Promise.all([
      import('firebase/auth'),
      import('./firebaseConfig'),
    ]).then(([firebaseAuth, firebaseConfigModule]) => ({
      ...firebaseAuth,
      ...firebaseConfigModule,
    }));
  }

  return firebaseClientPromise;
}

function hasPaymentSuccessParams() {
  if (typeof window === 'undefined') {
    return false;
  }

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('payment_success') === 'true' && Boolean(urlParams.get('session_id'));
}

function hasStoredAuthSessionHint() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(AUTH_SESSION_HINT_KEY) === 'true';
  } catch {
    return false;
  }
}

function setStoredAuthSessionHint(isAuthenticated) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (isAuthenticated) {
      window.localStorage.setItem(AUTH_SESSION_HINT_KEY, 'true');
      return;
    }

    window.localStorage.removeItem(AUTH_SESSION_HINT_KEY);
  } catch {
    // Ignore storage failures; Firebase remains the source of truth.
  }
}

function getAnalyticsMeasurementId() {
  return process.env.REACT_APP_GA_TRACKING_ID || DEFAULT_GA_MEASUREMENT_ID;
}

function syncAnalyticsConsentPreference(preference) {
  if (typeof window === 'undefined') {
    return;
  }

  const measurementId = getAnalyticsMeasurementId();
  const analyticsAllowed = canLoadAnalytics(preference);

  window.__TRYINTERVIEW_GA_MEASUREMENT_ID = measurementId;
  window.__tryInterviewAnalyticsConsentGranted = analyticsAllowed;
  window[`ga-disable-${measurementId}`] = !analyticsAllowed;

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
}

function scheduleAnalyticsBootstrap(measurementId) {
  if (typeof window === 'undefined' || window.__tryInterviewAnalyticsBootstrapRequested) {
    return;
  }

  window.__tryInterviewAnalyticsBootstrapRequested = true;
  window.__TRYINTERVIEW_GA_MEASUREMENT_ID = measurementId;
  window.__tryInterviewAnalyticsConsentGranted = true;

  const appendLoaderScript = () => {
    if (document.getElementById('tryinterview-analytics-loader')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'tryinterview-analytics-loader';
    script.async = true;
    script.src = `${process.env.PUBLIC_URL}/analytics-loader.js`;
    document.body.appendChild(script);
  };

  const schedule = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(appendLoaderScript, { timeout: 3000 });
      return;
    }

    window.setTimeout(appendLoaderScript, 1800);
  };

  if (document.readyState === 'complete') {
    schedule();
    return;
  }

  window.addEventListener('load', schedule, { once: true });
}

function App() {
  const { showToast } = useToast();
  const [currentPage, setCurrentPage] = useState(() => getCurrentPageFromLocation());
  const [user, setUser] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(() => getCookieConsentPreference());
  const [loading, setLoading] = useState(() => (
    requiresAuth(getCurrentPageFromLocation()) ||
    hasPaymentSuccessParams() ||
    hasStoredAuthSessionHint()
  ));
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [profileBootstrapPending, setProfileBootstrapPending] = useState(false);
  const authCancelledRef = useRef(false);
  const authInitializationPromiseRef = useRef(null);
  const authUnsubscribeRef = useRef(() => {});
  const authInitializerRef = useRef(() => Promise.resolve(null));
  const profileBootstrapRunRef = useRef(0);
  const isPaymentSuccess = hasPaymentSuccessParams();
  const hasAuthSessionHint = hasStoredAuthSessionHint();
  const currentPageRequiresAuth = requiresAuth(currentPage);
  const currentPageIsStandaloneApp = isStandaloneAppPage(currentPage);
  const shouldShowBlockingLoader = loading || (
    !authChecked && (currentPageRequiresAuth || isPaymentSuccess || hasAuthSessionHint)
  );
  const shouldRenderPaymentSuccessPage = Boolean(
    user && isPaymentSuccess && !currentPageRequiresAuth
  );

  const syncAuthState = (nextUser) => {
    setUser(nextUser);
    setStoredAuthSessionHint(Boolean(nextUser));

    if (!nextUser) {
      setShowOnboarding(false);
      setProfileBootstrapPending(false);
      setLoading(false);
      return;
    }

    setProfileBootstrapPending(true);
    setShowOnboarding(false);
    setShowAuthModal(false);

    const activePage = getCurrentPageFromLocation();

    if (!isStandaloneAppPage(activePage) && activePage !== 'dashboard' && !hasPaymentSuccessParams()) {
      setCurrentPage('dashboard');
      navigateToPage('dashboard', { replace: true });
    }

    setLoading(false);
  };

  const ensureAuthInitialized = () => {
    if (authInitializationPromiseRef.current) {
      return authInitializationPromiseRef.current;
    }

    authInitializationPromiseRef.current = (async () => {
      try {
        const { auth, firebaseConfigError, onAuthStateChanged } = await loadFirebaseClient();

        if (authCancelledRef.current) {
          return null;
        }

        if (!auth) {
          if (firebaseConfigError) {
            console.error('Authentication is disabled:', firebaseConfigError);
          }

          setAuthChecked(true);
          setLoading(false);
          return null;
        }

        authUnsubscribeRef.current = onAuthStateChanged(auth, (nextUser) => {
          syncAuthState(nextUser);
          setAuthChecked(true);
        });

        return auth;
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        authInitializationPromiseRef.current = null;

        if (!authCancelledRef.current) {
          setAuthChecked(true);
          setLoading(false);
        }

        return null;
      }
    })();

    return authInitializationPromiseRef.current;
  };

  authInitializerRef.current = ensureAuthInitialized;

  useEffect(() => {
    const initialPage = getCurrentPageFromLocation();

    if (requiresAuth(initialPage) || hasPaymentSuccessParams() || hasStoredAuthSessionHint()) {
      preloadComponents(SECURE_ROUTE_COMPONENTS);
      void authInitializerRef.current();
      return undefined;
    }

    return scheduleDeferredTask(() => {
      void authInitializerRef.current();
    }, { timeout: 2200, delay: 180 });
  }, []);

  useEffect(() => {
    authCancelledRef.current = false;

    return () => {
      authCancelledRef.current = true;
      authUnsubscribeRef.current();
    };
  }, []);

  useEffect(() => scheduleDeferredTask(() => {
    preloadComponents(PUBLIC_ROUTE_COMPONENTS);
  }, { timeout: 2400, delay: 220 }), []);

  useEffect(() => {
    if (!showAuthModal && !currentPageRequiresAuth && !isPaymentSuccess && !user) {
      return;
    }

    preloadComponents(SECURE_ROUTE_COMPONENTS);
    void authInitializerRef.current();
  }, [currentPageRequiresAuth, isPaymentSuccess, showAuthModal, user]);

  useEffect(() => {
    if (!authChecked || !user) {
      return undefined;
    }

    let cancelled = false;
    const runId = profileBootstrapRunRef.current + 1;
    profileBootstrapRunRef.current = runId;

    const bootstrapAuthenticatedUser = async () => {
      try {
        setProfileBootstrapPending(true);

        const [
          sessionService,
          userProfileService,
        ] = await Promise.all([
          import('./services/sessionService'),
          import('./services/userProfileService'),
        ]);

        await sessionService.ensureBackendSession(user).catch((error) => {
          console.warn('Backend session bootstrap did not complete cleanly:', error);
        });
        let profileResponse = null;
        let lastProfileBootstrapError = null;

        try {
          const syncedProfileResponse = await userProfileService.syncAuthenticatedUserProfile(user);
          if (syncedProfileResponse?.user) {
            profileResponse = syncedProfileResponse;
          }
        } catch (error) {
          lastProfileBootstrapError = error;
          console.warn('Initial profile sync failed, falling back to profile fetch:', error);
        }

        if (!profileResponse?.user) {
          const profileFetchAttempts = [0, 350];

          for (const retryDelayMs of profileFetchAttempts) {
            if (retryDelayMs > 0) {
              await new Promise((resolve) => {
                window.setTimeout(resolve, retryDelayMs);
              });
            }

            try {
              const fetchedProfileResponse = await userProfileService.getUserProfile(user);

              if (fetchedProfileResponse?.user) {
                profileResponse = fetchedProfileResponse;
                break;
              }
            } catch (error) {
              lastProfileBootstrapError = error;
              console.warn('Profile fetch attempt failed during auth bootstrap:', error);
            }
          }
        }

        if (!profileResponse?.user) {
          throw lastProfileBootstrapError || new Error('Unable to load the authenticated profile.');
        }

        if (cancelled || profileBootstrapRunRef.current !== runId) {
          return;
        }

        setShowOnboarding(shouldRequireOnboarding(profileResponse?.user, user));
        setShowAuthModal(false);
      } catch (error) {
        if (cancelled || profileBootstrapRunRef.current !== runId) {
          return;
        }

        console.error('Failed to bootstrap authenticated profile state:', error);
        setShowOnboarding(true);
        setShowAuthModal(false);
      } finally {
        if (!cancelled && profileBootstrapRunRef.current === runId) {
          setProfileBootstrapPending(false);
          setLoading(false);
        }
      }
    };

    void bootstrapAuthenticatedUser();

    return () => {
      cancelled = true;
    };
  }, [authChecked, user]);

  useEffect(() => {
    upgradeLegacyHashRoute();

    const handleLocationChange = () => {
      const nextPage = getCurrentPageFromLocation();
      setCurrentPage((previousPage) => (
        previousPage === nextPage ? previousPage : nextPage
      ));
    };

    handleLocationChange();
    window.addEventListener('popstate', handleLocationChange);

    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  useEffect(() => {
    const syncConsentFromStorage = () => {
      setCookieConsent(getCookieConsentPreference());
    };

    window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, syncConsentFromStorage);
    window.addEventListener('storage', syncConsentFromStorage);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, syncConsentFromStorage);
      window.removeEventListener('storage', syncConsentFromStorage);
    };
  }, []);

  useEffect(() => {
    syncAnalyticsConsentPreference(cookieConsent);

    if (canLoadAnalytics(cookieConsent)) {
      scheduleAnalyticsBootstrap(getAnalyticsMeasurementId());
    }
  }, [cookieConsent]);

  useEffect(() => {
    if (profileBootstrapPending) {
      return;
    }

    if (!currentPageRequiresAuth && !isPaymentSuccess) {
      setLoading(false);
      return;
    }

    if (!authChecked) {
      setLoading(true);
      void authInitializerRef.current();
      return;
    }

    if (!user) {
      setLoading(true);
      return;
    }

    setLoading(false);
  }, [authChecked, currentPageRequiresAuth, isPaymentSuccess, profileBootstrapPending, user]);

  useEffect(() => {
    if (!authChecked || user) {
      return;
    }

    if (!currentPageRequiresAuth && !isPaymentSuccess) {
      return;
    }

    setShowAuthModal(true);
    setShowOnboarding(false);
    setCurrentPage('home');
    navigateToPage('home', { replace: true });
  }, [authChecked, currentPageRequiresAuth, isPaymentSuccess, user]);

  useEffect(() => {
    if (!authChecked || !user || showOnboarding || profileBootstrapPending) {
      return;
    }

    if (!currentPageIsStandaloneApp && currentPage !== 'dashboard') {
      setCurrentPage('dashboard');
      navigateToPage('dashboard', { replace: true });
    }
  }, [authChecked, currentPage, currentPageIsStandaloneApp, profileBootstrapPending, showOnboarding, user]);

  const handleGetStarted = () => {
    preloadComponents(SECURE_ROUTE_COMPONENTS);
    void authInitializerRef.current();
    setShowAuthModal(true);
  };

  const handleAuthSuccess = (_, isNewUser = false) => {
    setShowAuthModal(false);
    showToast({
      variant: 'success',
      title: isNewUser ? 'Account created' : 'Signed in',
      message: isNewUser
        ? 'Welcome to TryInterview. Let’s finish setting up your prep workspace.'
        : 'Welcome back to TryInterview.',
    });
  };

  const handleOnboardingComplete = async (data) => {
    if (!user) {
      throw new Error('User must be signed in to complete onboarding');
    }

    const { completeUserOnboarding } = await import('./services/userProfileService');
    return completeUserOnboarding(user, data);
  };

  const handleOnboardingFinish = () => {
    setShowOnboarding(false);
    showToast({
      variant: 'success',
      title: 'Onboarding complete',
      message: 'Your interview prep workspace is ready.',
    });
    // Force a reload so the dashboard fetches the newly saved profile image and details
    window.location.reload();
  };

  const handleCloseModal = () => setShowAuthModal(false);

  const handleAcceptAllCookies = () => {
    setCookieConsent(persistCookieConsentPreference(COOKIE_CONSENT_ACCEPT_ALL));
  };

  const handleAcceptEssentialCookies = () => {
    setCookieConsent(persistCookieConsentPreference(COOKIE_CONSENT_ESSENTIAL_ONLY));
  };

  const handleLogout = async () => {
    try {
      const { auth, signOut } = await loadFirebaseClient();
      const { destroyBackendSession } = await import('./services/sessionService');

      if (!auth) {
        return;
      }

      await destroyBackendSession(user).catch((error) => {
        console.warn('Backend session logout did not complete cleanly:', error);
      });
      await signOut(auth);
      setStoredAuthSessionHint(false);
      setUser(null);
      setCurrentPage('home');
      setShowOnboarding(false);
      setLoading(false);
      navigateToPage('home', { replace: true });
      showToast({
        variant: 'info',
        title: 'Signed out',
        message: 'You have been logged out successfully.',
      });
    } catch {
      showToast({
        variant: 'error',
        title: 'Logout failed',
        message: 'Please try signing out again.',
      });
    }
  };

  const handleBackToHome = () => {
    navigateToPage('home');
    setCurrentPage('home');
  };

  if (shouldShowBlockingLoader) {
    return <Loading />;
  }

  if (shouldRenderPaymentSuccessPage) {
    return (
      <div className="App">
        <Suspense fallback={<Loading />}>
          <PaymentSuccess user={user} />
        </Suspense>
      </div>
    );
  }

  if (currentPage === 'verify') {
    return (
      <div className="App">
        <Suspense fallback={<Loading />}>
          <CertificateVerification onGetStarted={handleGetStarted} />
        </Suspense>
      </div>
    );
  }

  if (showOnboarding && user) {
    return (
      <div className="App">
        <Suspense fallback={<Loading />}>
          <OnboardingModal
            user={user}
            onComplete={handleOnboardingComplete}
            onFinish={handleOnboardingFinish}
          />
        </Suspense>
      </div>
    );
  }

  if (authChecked && user && !showOnboarding && !currentPageIsStandaloneApp) {
    return (
      <div className="App">
        <Suspense fallback={<Loading />}>
          <Dashboard user={user} onLogout={handleLogout} />
        </Suspense>
      </div>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'features':
        return <Features onBack={handleBackToHome} onGetStarted={handleGetStarted} />;
      case 'founder':
        return <Founder onBack={handleBackToHome} />;
      case 'terms':
        return <Terms onGetStarted={handleGetStarted} />;
      case 'about':
        return <About onGetStarted={handleGetStarted} />;
      case 'privacy':
        return <Privacy onGetStarted={handleGetStarted} />;
      case 'question-bank':
        return user
          ? <QuestionBank onBack={handleBackToHome} user={user} />
          : <LandingPage onGetStarted={handleGetStarted} />;
      case 'resume-analyzer':
        return user
          ? <ResumeAnalyzer onBack={handleBackToHome} user={user} />
          : <LandingPage onGetStarted={handleGetStarted} />;
      case 'contact':
        return <Contact onGetStarted={handleGetStarted} />;
      case 'pricing':
        return <Pricing onGetStarted={handleGetStarted} />;
      case 'sitemap':
        return <Sitemap onGetStarted={handleGetStarted} />;
      case 'help':
        return <Help onGetStarted={handleGetStarted} />;
      case 'faq':
        return <FAQ onGetStarted={handleGetStarted} />;
      case 'cookies':
        return <Cookies onGetStarted={handleGetStarted} />;
      case 'verify':
        return <CertificateVerification onGetStarted={handleGetStarted} />;
      default:
        return <LandingPage onGetStarted={handleGetStarted} />;
    }
  };

  return (
    <div className="App">
      <Suspense fallback={<Loading />}>
        {renderPage()}
      </Suspense>
      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal
            onClose={handleCloseModal}
            onAuthSuccess={handleAuthSuccess}
          />
        </Suspense>
      )}
      {!cookieConsent && !user && (
        <Suspense fallback={null}>
          <CookieConsentBanner
            onAcceptAll={handleAcceptAllCookies}
            onAcceptEssentialOnly={handleAcceptEssentialCookies}
          />
        </Suspense>
      )}
    </div>
  );
}

export default App;
